/**
 * NotificationPermissionBanner Component
 * Prompts users to enable browser notifications for task reminders
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellOff, X, Check } from 'lucide-react';
import {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  showNotification
} from '@/services/notificationService';
import {
  isPushSupported,
  registerServiceWorker,
  subscribeToPush
} from '@/services/pushService';

const DISMISSED_KEY = 'finlist_notification_banner_dismissed';

export default function NotificationPermissionBanner() {
  const [visible, setVisible] = useState(false);
  const [permission, setPermission] = useState('default');
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    // Check if notifications are supported
    if (!isNotificationSupported()) {
      setVisible(false);
      return;
    }

    const currentPermission = getNotificationPermission();
    setPermission(currentPermission);

    // Don't show if already granted or denied
    if (currentPermission === 'granted' || currentPermission === 'denied') {
      setVisible(false);
      return;
    }

    // Check if user dismissed the banner recently (within 7 days)
    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const now = new Date();
      const daysSinceDismissed = (now - dismissedDate) / (1000 * 60 * 60 * 24);
      
      if (daysSinceDismissed < 7) {
        setVisible(false);
        return;
      }
    }

    // Show the banner
    setVisible(true);
  }, []);

  const handleEnable = async () => {
    setRequesting(true);
    try {
      // First, request browser notification permission
      const result = await requestNotificationPermission();
      setPermission(result);
      
      if (result === 'granted') {
        // After permission granted, subscribe to push notifications for background reminders
        if (isPushSupported()) {
          try {
            await registerServiceWorker();
            await subscribeToPush();
            console.log('[Notifications] Push subscription registered successfully');
          } catch (pushError) {
            console.warn('[Notifications] Push subscription failed (will use browser notifications only):', pushError);
            // Don't fail - browser notifications will still work when app is open
          }
        }
        // Give immediate feedback with an in-app notification so users see success even if push is skipped
        showNotification('Notifications enabled', {
          body: 'FinList will keep your task reminders active.',
          tag: 'finlist-notify-test'
        });
        setVisible(false);
      } else if (result === 'denied') {
        setVisible(false);
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
    } finally {
      setRequesting(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, new Date().toISOString());
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 border-2 border-purple-200 dark:border-purple-700 rounded-2xl p-4 mb-4"
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="flex-shrink-0 w-10 h-10 bg-purple-100 dark:bg-purple-900/50 rounded-xl flex items-center justify-center">
            <Bell size={20} className="text-purple-600 dark:text-purple-400" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              Enable Task Reminders
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              Get notified when your tasks are due. Never miss a deadline!
            </p>

            {/* Actions */}
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleEnable}
                disabled={requesting}
                className="px-4 py-2 text-xs font-semibold text-white bg-purple-600 dark:bg-purple-500 rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                {requesting ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Enabling...
                  </>
                ) : (
                  <>
                    <Check size={14} />
                    Enable Notifications
                  </>
                )}
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
              >
                Maybe Later
              </button>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 hover:bg-purple-100 dark:hover:bg-purple-900/50 rounded-lg transition-colors"
            aria-label="Dismiss"
          >
            <X size={16} className="text-gray-400 dark:text-gray-500" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// Compact version for settings or header
export function NotificationToggle({ className = '' }) {
  const [permission, setPermission] = useState('default');
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    if (isNotificationSupported()) {
      setPermission(getNotificationPermission());
    }
  }, []);

  const handleToggle = async () => {
    if (permission === 'granted') {
      // Can't revoke programmatically - show info
      alert('To disable notifications, use your browser settings.');
      return;
    }

    setRequesting(true);
    try {
      const result = await requestNotificationPermission();
      setPermission(result);
      
      // If granted, also subscribe to push notifications
      if (result === 'granted' && isPushSupported()) {
        try {
          await registerServiceWorker();
          await subscribeToPush();
          console.log('[Notifications] Push subscription registered via toggle');
        } catch (pushError) {
          console.warn('[Notifications] Push subscription failed:', pushError);
        }
      }
    } finally {
      setRequesting(false);
    }
  };

  if (!isNotificationSupported()) {
    return (
      <div className={`flex items-center gap-2 text-sm text-gray-500 ${className}`}>
        <BellOff size={16} />
        <span>Notifications not supported</span>
      </div>
    );
  }

  return (
    <button
      onClick={handleToggle}
      disabled={requesting || permission === 'denied'}
      className={`flex items-center gap-2 text-sm font-medium transition-colors ${className} ${
        permission === 'granted'
          ? 'text-green-600'
          : permission === 'denied'
          ? 'text-red-500 cursor-not-allowed'
          : 'text-gray-600 hover:text-purple-600'
      }`}
    >
      {permission === 'granted' ? (
        <>
          <Bell size={16} className="text-green-600" />
          <span>Notifications On</span>
        </>
      ) : permission === 'denied' ? (
        <>
          <BellOff size={16} />
          <span>Notifications Blocked</span>
        </>
      ) : (
        <>
          <Bell size={16} />
          <span>{requesting ? 'Enabling...' : 'Enable Notifications'}</span>
        </>
      )}
    </button>
  );
}
